import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp,
  Users,
  DollarSign,
  FolderKanban,
  CheckSquare,
  TicketCheck,
  FileText,
  Target,
  Handshake,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const { currencySymbol } = useSettings();
  const [stats, setStats] = useState(null);
  const [dealsByStage, setDealsByStage] = useState({});
  const [tasksByStatus, setTasksByStatus] = useState({});
  const [leadsBySource, setLeadsBySource] = useState([]);
  const [expensesByCategory, setExpensesByCategory] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [statsRes, dealsRes, tasksRes, leadsRes, expensesRes, activityRes] = await Promise.all([
        axios.get(`${API_URL}/api/reports/dashboard`),
        axios.get(`${API_URL}/api/reports/deals-by-stage`),
        axios.get(`${API_URL}/api/reports/tasks-by-status`),
        axios.get(`${API_URL}/api/reports/leads-by-source`),
        axios.get(`${API_URL}/api/reports/expenses-by-category`),
        axios.get(`${API_URL}/api/reports/recent-activity`)
      ]);
      
      setStats(statsRes.data);
      setDealsByStage(dealsRes.data);
      setTasksByStatus(tasksRes.data);
      setLeadsBySource(leadsRes.data);
      setExpensesByCategory(expensesRes.data);
      setRecentActivity(activityRes.data);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return <div className="flex items-center justify-center h-64">Loading reports...</div>;
  }

  const stageLabels = {
    qualification: 'Qualification',
    proposal: 'Proposal',
    negotiation: 'Negotiation',
    closed_won: 'Won',
    closed_lost: 'Lost'
  };

  const stageColors = {
    qualification: 'bg-slate-500',
    proposal: 'bg-blue-500',
    negotiation: 'bg-amber-500',
    closed_won: 'bg-emerald-500',
    closed_lost: 'bg-red-500'
  };

  const sourceLabels = {
    website: 'Website',
    referral: 'Referral',
    cold_call: 'Cold Call',
    linkedin: 'LinkedIn',
    event: 'Event',
    advertisement: 'Ads',
    other: 'Other'
  };

  const categoryLabels = {
    travel: 'Travel',
    meals: 'Meals',
    software: 'Software',
    hardware: 'Hardware',
    office: 'Office',
    marketing: 'Marketing',
    other: 'Other'
  };

  const maxDealValue = Math.max(...Object.values(dealsByStage).map(d => d.value || 0), 1);
  const maxTaskCount = Math.max(...Object.values(tasksByStatus), 1);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reports & Analytics</h1>
        <p className="text-muted-foreground">Overview of your business performance.</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-blue-500" />
              <span className="text-sm text-muted-foreground">Clients</span>
            </div>
            <div className="text-2xl font-bold">{stats.crm.clients.total}</div>
            <div className="text-xs text-emerald-600 flex items-center gap-1">
              <ArrowUpRight className="h-3 w-3" />
              {stats.crm.clients.active} active
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-orange-500" />
              <span className="text-sm text-muted-foreground">Leads</span>
            </div>
            <div className="text-2xl font-bold">{stats.crm.leads.total}</div>
            <div className="text-xs text-blue-600">{stats.crm.leads.new} new, {stats.crm.leads.qualified} qualified</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Handshake className="h-4 w-4 text-purple-500" />
              <span className="text-sm text-muted-foreground">Deals</span>
            </div>
            <div className="text-2xl font-bold">{stats.crm.deals.total}</div>
            <div className="text-xs text-emerald-600">{stats.crm.deals.win_rate}% win rate</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-4 w-4 text-emerald-500" />
              <span className="text-sm text-muted-foreground">Pipeline</span>
            </div>
            <div className="text-2xl font-bold">{currencySymbol} {(stats.crm.deals.pipeline_value / 1000).toFixed(0)}K</div>
            <div className="text-xs text-muted-foreground">{currencySymbol} {(stats.crm.deals.won_value / 1000).toFixed(0)}K won</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <FolderKanban className="h-4 w-4 text-indigo-500" />
              <span className="text-sm text-muted-foreground">Projects</span>
            </div>
            <div className="text-2xl font-bold">{stats.projects.total}</div>
            <div className="text-xs text-blue-600">{stats.projects.active} active</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <CheckSquare className="h-4 w-4 text-teal-500" />
              <span className="text-sm text-muted-foreground">Tasks</span>
            </div>
            <div className="text-2xl font-bold">{stats.tasks.total}</div>
            <div className="text-xs text-emerald-600">{stats.tasks.completion_rate}% complete</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Deal Pipeline */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Deal Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(dealsByStage).map(([stage, data]) => (
                <div key={stage} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{stageLabels[stage]}</span>
                    <span className="text-muted-foreground">
                      {data.count} deals · {currencySymbol} {data.value?.toLocaleString() || 0}
                    </span>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${stageColors[stage]} transition-all`}
                      style={{ width: `${(data.value / maxDealValue) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Task Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5" />
              Task Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(tasksByStatus).map(([status, count]) => {
                const labels = { todo: 'To Do', in_progress: 'In Progress', review: 'Review', completed: 'Completed' };
                const colors = { todo: 'bg-slate-500', in_progress: 'bg-blue-500', review: 'bg-amber-500', completed: 'bg-emerald-500' };
                return (
                  <div key={status} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{labels[status]}</span>
                      <span className="text-muted-foreground">{count} tasks</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${colors[status]} transition-all`}
                        style={{ width: `${(count / maxTaskCount) * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Leads by Source */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Leads by Source
            </CardTitle>
          </CardHeader>
          <CardContent>
            {leadsBySource.length > 0 ? (
              <div className="space-y-3">
                {leadsBySource.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-sm">{sourceLabels[item.source] || item.source}</span>
                    <Badge variant="secondary">{item.count}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-4">No lead data</p>
            )}
          </CardContent>
        </Card>

        {/* Expenses by Category */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Expenses by Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            {expensesByCategory.length > 0 ? (
              <div className="space-y-3">
                {expensesByCategory.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-sm">{categoryLabels[item.category] || item.category}</span>
                    <span className="font-mono text-sm">{currencySymbol} {item.total?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm text-center py-4">No expense data</p>
            )}
          </CardContent>
        </Card>

        {/* Financial Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Financial Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Invoiced</span>
                <span className="font-mono font-semibold">{currencySymbol} {stats.finance.total_invoiced?.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Paid</span>
                <span className="font-mono font-semibold text-emerald-600">{currencySymbol} {stats.finance.paid?.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Pending</span>
                <span className="font-mono font-semibold text-amber-600">{currencySymbol} {stats.finance.pending?.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Expenses</span>
                <span className="font-mono font-semibold text-red-600">-{currencySymbol} {stats.finance.total_expenses?.toLocaleString()}</span>
              </div>
              <div className="border-t pt-3 flex items-center justify-between">
                <span className="font-medium">Net Revenue</span>
                <span className={`font-mono font-bold text-lg ${stats.finance.net_revenue >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {currencySymbol} {stats.finance.net_revenue?.toLocaleString()}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {recentActivity.length > 0 ? (
            <div className="space-y-4">
              {recentActivity.map((activity, idx) => (
                <div key={idx} className="flex items-center gap-4 pb-4 border-b last:border-0 last:pb-0">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    activity.type === 'deal' ? 'bg-purple-100 text-purple-600' :
                    activity.type === 'client' ? 'bg-blue-100 text-blue-600' :
                    'bg-amber-100 text-amber-600'
                  }`}>
                    {activity.type === 'deal' ? <Handshake className="h-5 w-5" /> :
                     activity.type === 'client' ? <Users className="h-5 w-5" /> :
                     <TicketCheck className="h-5 w-5" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{activity.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(activity.timestamp), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                  {activity.value && (
                    <span className="font-mono text-sm font-semibold">{currencySymbol} {activity.value?.toLocaleString()}</span>
                  )}
                  {activity.status && (
                    <Badge variant="outline">{activity.status}</Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">No recent activity</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;